import { ChevronsUpDown, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";
import {
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
} from "~/components/ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";

import { trpc } from "~/api/trpc";
import { cn } from "~/lib/utils";
import { orderedCountries } from "~/helper/form/dialCodes";
import { Customer } from "~/stores/create-appointment-store";

const nameRegex = /^[a-zA-ZäöüßÄÖÜẞ\- ]{2,50}$/;
const phoneRegex = /^[\+]?[\d\s\(\)\-\.]{5,25}$/;
const mailRegex =
    /^[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;

type FormFields = {
    firstName: string;
    lastName: string;
    phone: string;
    countryCodePhone: string;
    email: string;
    category: "STANDARD" | "PREMIUM";
    termsAccepted: boolean;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onCustomerCreated: (customer: Customer) => void;
};

const CreateCustomerModal = ({ open, onCustomerCreated, onClose }: Props) => {
    const utils = trpc.useUtils();
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [countryOpen, setCountryOpen] = useState(false);

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors },
    } = useForm<FormFields>({
        defaultValues: {
            firstName: "",
            lastName: "",
            phone: "",
            countryCodePhone: "+49",
            email: "",
            category: "STANDARD",
            termsAccepted: true,
        },
    });

    const createCustomerMutation = trpc.customers.create.useMutation({
        onSuccess: (customer) => {
            utils.customers.search.invalidate();
            utils.customers.getAll.invalidate();
            toast.success("Kunde erfolgreich angelegt");
            onCustomerCreated({
                id: customer.id,
                firstName: customer.firstName,
                lastName: customer.lastName,
                email: customer.email || null,
                phone: customer.phone,
                birthday: null,
                country: customer.country,
                preferredCommunicationType: customer.preferredCommunicationType,
                category: customer.category,
                city: customer.city || null,
                zipCode: customer.zipCode || null,
                street: customer.street || null,
                streetNumber: customer.streetNumber || null,
                alternativePhone: customer.alternativePhone || null,
                note: customer.note || null,
            });
            reset();
            onClose();
        },
        onError: (error) => {
            if (error.data?.code === "CONFLICT" || error.message.includes("existiert bereits")) {
                setErrorMessage(error.message);
                toast.error("Kunde existiert bereits.");
            } else {
                toast.error("Fehler beim Erstellen", { description: error.message });
            }
        },
    });

    const category = watch("category");
    const termsAccepted = watch("termsAccepted");
    const countryCodePhone = watch("countryCodePhone");

    const onSubmit = (data: FormFields) => {
        setErrorMessage("");
        const fullPhone = `${data.countryCodePhone}${data.phone}`;
        createCustomerMutation.mutate({
            firstName: data.firstName,
            lastName: data.lastName,
            phone: fullPhone,
            email: data.email,
            category: data.category,
            termsAccepted: data.termsAccepted,
            preferredCommunicationType: "EMAIL",
            country: "GERMANY",
            cooperation: false,
        });
    };

    const validateName = (value: string) => {
        if (!value || value.length < 2) {
            return "Mindestens 2 Zeichen erforderlich";
        }
        if (value.length > 50) {
            return "Maximal 50 Zeichen erlaubt";
        }
        if (!nameRegex.test(value)) {
            return "Bitte geben Sie einen gültigen Namen ein";
        }
        return true;
    };

    const validatePhone = (value: string) => {
        if (!value) {
            return "Telefonnummer ist erforderlich";
        }
        if (!phoneRegex.test(value)) {
            return "Bitte geben Sie eine gültige Telefonnummer ein";
        }
        return true;
    };

    const validateEmail = (value: string) => {
        if (!value) {
            return "E-Mail ist erforderlich";
        }
        if (!mailRegex.test(value)) {
            return "Bitte geben Sie eine gültige E-Mail ein";
        }
        return true;
    };

    const isLoading = createCustomerMutation.isPending;

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Kunde anlegen</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {/* First Name */}
                    <div className="space-y-2">
                        <Label htmlFor="firstName" className={cn(errors.firstName && "text-red-500")}>
                            Vorname <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="firstName"
                            {...register("firstName", { validate: validateName })}
                            maxLength={50}
                            className={cn(errors.firstName && "border-red-500")}
                        />
                        {errors.firstName && (
                            <p className="text-sm text-red-500">{errors.firstName.message}</p>
                        )}
                    </div>

                    {/* Last Name */}
                    <div className="space-y-2">
                        <Label htmlFor="lastName" className={cn(errors.lastName && "text-red-500")}>
                            Nachname <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="lastName"
                            {...register("lastName", { validate: validateName })}
                            maxLength={50}
                            className={cn(errors.lastName && "border-red-500")}
                        />
                        {errors.lastName && (
                            <p className="text-sm text-red-500">{errors.lastName.message}</p>
                        )}
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                        <Label htmlFor="phone" className={cn(errors.phone && "text-red-500")}>
                            Telefonnummer <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex gap-2">
                            <Popover open={countryOpen} onOpenChange={setCountryOpen} modal>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        role="combobox"
                                        aria-expanded={countryOpen}
                                        className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-28 items-center justify-between gap-1 rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                                    >
                                        {(() => {
                                            const selectedCountry =
                                                orderedCountries.find(
                                                    (c) => c.dial_code === countryCodePhone,
                                                ) ?? orderedCountries[0];
                                            return (
                                                <span className="flex items-center gap-1.5 truncate">
                                                    <span>{selectedCountry.emoji}</span>
                                                    <span>{selectedCountry.dial_code}</span>
                                                </span>
                                            );
                                        })()}
                                        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-64 p-0" align="start">
                                    <Command>
                                        <CommandInput placeholder="Land suchen" />
                                        <CommandList>
                                            <CommandEmpty>Kein Land gefunden</CommandEmpty>
                                            {orderedCountries.map((country) => (
                                                <CommandItem
                                                    key={country.code}
                                                    value={country.code}
                                                    keywords={[country.name, country.dial_code]}
                                                    onSelect={() => {
                                                        setValue("countryCodePhone", country.dial_code);
                                                        setCountryOpen(false);
                                                    }}
                                                >
                                                    <span className="mr-1">{country.emoji}</span>
                                                    <span className="flex-1 truncate">
                                                        {country.name}
                                                    </span>
                                                    <span className="text-muted-foreground text-sm">
                                                        {country.dial_code}
                                                    </span>
                                                </CommandItem>
                                            ))}
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                            <Input
                                id="phone"
                                {...register("phone", { validate: validatePhone })}
                                className={cn("flex-1", errors.phone && "border-red-500")}
                                placeholder="123 456789"
                            />
                        </div>
                        {errors.phone && (
                            <p className="text-sm text-red-500">{errors.phone.message}</p>
                        )}
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                        <Label htmlFor="email" className={cn(errors.email && "text-red-500")}>
                            E-Mail <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="email"
                            type="email"
                            {...register("email", { validate: validateEmail })}
                            className={cn(errors.email && "border-red-500")}
                        />
                        {errors.email && (
                            <p className="text-sm text-red-500">{errors.email.message}</p>
                        )}
                    </div>

                    {/* Category */}
                    <div className="space-y-2">
                        <Label htmlFor="category">
                            Kategorie <span className="text-red-500">*</span>
                        </Label>
                        <Select
                            value={category}
                            onValueChange={(value: "STANDARD" | "PREMIUM") => setValue("category", value)}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Kategorie auswählen" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="STANDARD">Standard</SelectItem>
                                <SelectItem value="PREMIUM">Premium</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Communication Channel - Disabled for now */}
                    <div className="space-y-2">
                        <Label className="text-muted-foreground">
                            Kommunikationskanal
                        </Label>
                        <Select disabled value="EMAIL">
                            <SelectTrigger className="opacity-60">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="EMAIL">E-Mail</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                            Aktuell werden nur E-Mails unterstützt.
                        </p>
                    </div>

                    {/* Terms Accepted */}
                    <div className="flex items-center space-x-2">
                        <Checkbox
                            id="termsAccepted"
                            checked={termsAccepted}
                            onCheckedChange={(checked) => setValue("termsAccepted", !!checked)}
                        />
                        <Label
                            htmlFor="termsAccepted"
                            className="text-sm font-normal cursor-pointer"
                        >
                            AGBs gelesen & akzeptiert
                        </Label>
                    </div>

                    <DialogFooter>
                        <div className="flex flex-col gap-2 w-full">
                            <div className="flex flex-row justify-end gap-2">
                                <Button type="button" variant="outline" onClick={onClose}>
                                    Abbrechen
                                </Button>
                                <Button type="submit" disabled={isLoading}>
                                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Erstellen
                                </Button>
                            </div>
                            {errorMessage.length > 0 && (
                                <span className="text-xs text-red-500 text-right">{errorMessage}</span>
                            )}
                        </div>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default CreateCustomerModal;
